import { useState, useCallback, useMemo } from "react";
import { Task, Link } from "@dhx/react-gantt";
import dayjs from "dayjs";
import {
  useGetSchedules,
  useCreateSchedule,
  useUpdateSchedule,
  useUpdateMultipleSchedules,
  useDeleteSchedule,
  useCreateLink,
  useDeleteLink,
} from "../mutations";
import {
  PhaseFormData,
  ActivityFormData,
  StageGateFormData,
} from "../components";
import {
  formatDateForAPI,
  getStatusFromProgress,
  calculateEndDate,
} from "../utils/transformers";
import {
  CreateSchedulingItemPayload,
  UpdateSchedulingItemPayload,
  SchedulingItem,
  DependencyType,
} from "../types/scheduling";
import { useProjects } from "../../projects/hooks/useProjects";
import { useOperations } from "../../recurringTasks/hooks/useOperations";
import { useOperationsList } from "../../recurringTasks/hooks/useOperationsList";
import { useCapabilities } from "../../../hooks/useCapabilities";
import { useSearchParams } from "react-router-dom";

type DrawerType = "phase" | "activity" | "stageGate";

export const useScheduleEditor = (projectId: number) => {

  const { can } = useCapabilities();

  const isAllowedToAddActivity = can("operational_delivery:project:activity:add");

  const { data, isLoading, isError, error, refetch } = useGetSchedules(projectId);

  const createMutation = useCreateSchedule(projectId);
  const updateMutation = useUpdateSchedule(projectId);
  const updateMultipleMutation = useUpdateMultipleSchedules(projectId);
  const deleteMutation = useDeleteSchedule(projectId);
  const createLinkMutation = useCreateLink(projectId);
  const deleteLinkMutation = useDeleteLink(projectId);

  const schedules = data?.data?.rows || [];
  const links = data?.data?.links || [];

  const createSchedule = useCallback(
    async (payload: CreateSchedulingItemPayload) => {
      try {
        const response = await createMutation.mutateAsync(payload);
        return response.data.row;
      } catch (error) {
        throw error;
      }
    },
    [createMutation]
  );

  const updateSchedule = useCallback(
    async (
      scheduleId: number,
      payload: Partial<UpdateSchedulingItemPayload>
    ) => {
      try {
        const response = await updateMutation.mutateAsync({
          scheduleId,
          payload,
        });
        return response.data.row;
      } catch (error) {
        throw error;
      }
    },
    [updateMutation]
  );

  const updateMultipleSchedules = useCallback(
    async (items: UpdateSchedulingItemPayload[]) => {
      try {
        return await updateMultipleMutation.mutateAsync({ items });
      } catch (error) {
        throw error;
      }
    },
    [updateMultipleMutation]
  );

  const deleteSchedule = useCallback(
    async (scheduleId: number) => {
      try {
        return await deleteMutation.mutateAsync(scheduleId);
      } catch (error) {
        throw error;
      }
    },
    [deleteMutation]
  );

  const getScheduleById = useCallback(
    (scheduleId: number): SchedulingItem | undefined => {
      return schedules.find((schedule) => schedule.id === scheduleId);
    },
    [schedules]
  );

  const getSchedulesByParentId = useCallback(
    (parentId: number | null): SchedulingItem[] => {
      return schedules.filter((schedule) => schedule.parent_id === parentId);
    },
    [schedules]
  );

  const getSchedulesByType = useCallback(
    (type: "phase" | "activity" | "stage_gate"): SchedulingItem[] => {
      return schedules.filter((schedule) => schedule.type === type);
    },
    [schedules]
  );

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();

  const id = searchParams.get("id");
  const type = searchParams.get("type");

  const editingScheduleItem = useMemo(() => schedules.find((s) => s.id === Number(id)) || null, [schedules, id]);

  const phaseOptions = useMemo(
    () => isAllowedToAddActivity ? schedules.filter((schedule) => schedule.type === "phase").map((phase) => ({ id: phase.id, name: phase.name })) :
      schedules.filter((schedule) => schedule.type === "phase").filter((phase) => {
        const hasApprovedStageGate = schedules.some(
          (schedule) =>
            schedule.type === "stage_gate" &&
            schedule.parent_id === phase.id &&
            schedule.is_decision_approved === true
        );
        return !hasApprovedStageGate;
      }).map((phase) => ({ id: phase.id, name: phase.name })),
    [schedules, isAllowedToAddActivity]
  );

  const phaseFormData = useMemo((): Partial<PhaseFormData> => {
    if (!editingScheduleItem) return {};
    return {
      phase: editingScheduleItem.name,
      startDate: editingScheduleItem.planned_start_date
        ? dayjs(editingScheduleItem.planned_start_date)
        : dayjs(),
      plannedCompletionDate: editingScheduleItem.planned_end_date
        ? dayjs(editingScheduleItem.planned_end_date)
        : dayjs(),
      progress: parseFloat(editingScheduleItem.progress_percentage) || 0,
      owner: editingScheduleItem.owner || "",
      userId: editingScheduleItem.user_id || undefined,
      status:
        editingScheduleItem.status === "not_started"
          ? "Not Started"
          : editingScheduleItem.status === "in_progress"
            ? "In Progress"
            : editingScheduleItem.status === "completed"
              ? "Completed"
              : "On Hold",
      color: editingScheduleItem.color || "#2196F3",
    };
  }, [editingScheduleItem]);

  const activityFormData = useMemo((): Partial<ActivityFormData> => {
    if (!editingScheduleItem) return {};
    const parentPhase = editingScheduleItem.parent_id
      ? getScheduleById(editingScheduleItem.parent_id)
      : null;
    return {
      title: editingScheduleItem.name,
      startDate: editingScheduleItem.planned_start_date
        ? dayjs(editingScheduleItem.planned_start_date)
        : dayjs(),
      plannedCompletionDate: editingScheduleItem.planned_end_date
        ? dayjs(editingScheduleItem.planned_end_date)
        : dayjs(),
      actualCompletionDate: editingScheduleItem.actual_end_date
        ? dayjs(editingScheduleItem.actual_end_date)
        : undefined,
      progress: parseFloat(editingScheduleItem.progress_percentage) || 0,
      owner: editingScheduleItem.owner || "",
      userId: editingScheduleItem.user_id || undefined,
      phase: parentPhase?.name || "",
      phaseId: editingScheduleItem.parent_id || undefined,
      category: editingScheduleItem.category_display_text || "",
      category_key: editingScheduleItem.category || undefined,
      is_recurring: editingScheduleItem.is_recurring || false,
      frequency: editingScheduleItem.frequency || "",
      description: editingScheduleItem.description || "",
      recurring_day: editingScheduleItem.recurring_day || [],
      asset_id: editingScheduleItem.asset_id || null,
      monthly_pattern: (editingScheduleItem.monthly_pattern as "day_of_month" | "day_of_week" | undefined) || undefined,
    };
  }, [editingScheduleItem, getScheduleById]);

  const stageGateFormData = useMemo((): Partial<StageGateFormData> => {
    if (!editingScheduleItem) return {};
    const parentPhase = editingScheduleItem.parent_id
      ? getScheduleById(editingScheduleItem.parent_id)
      : null;
    return {
      title: editingScheduleItem.name,
      plannedCompletionDate: editingScheduleItem.planned_end_date
        ? dayjs(editingScheduleItem.planned_end_date)
        : dayjs(),
      actualCompletionDate: editingScheduleItem.actual_end_date
        ? dayjs(editingScheduleItem.actual_end_date)
        : undefined,
      owner: editingScheduleItem.owner || "",
      userId: editingScheduleItem.user_id || undefined,
      description: editingScheduleItem.description || "",
      parentPhase: parentPhase?.name || "",
      parentPhaseId: editingScheduleItem.parent_id || undefined,
    };
  }, [editingScheduleItem, getScheduleById]);

  const handleOpenDrawer = ({ riskId, type }: { riskId: string, type: string }) => {
    const params = new URLSearchParams(searchParams);
    if (riskId) {
      params.set("id", riskId);
    }
    params.set("type", type);
    setSearchParams(params, { replace: true });
  };

  const handleDrawerClose = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    params.delete("id");
    params.delete("type");
    setSearchParams(params, { replace: true });
  }, []);

  const handleTaskDblClick = useCallback((task: Task) => {
    // setStageGateId(null);
    // setEditingScheduleItemId(Number(task.id) || null);
    // if (task.id) {
    //   setStageGateId(task.id as number);
    // }
    // if (task.type === "milestone") {
    //   setOpenDrawer("stageGate");
    // } else if (task.type === "project") {
    //   setOpenDrawer("phase");
    // } else {
    //   setOpenDrawer("activity");
    // }
    handleOpenDrawer({ riskId: task.id as string, type: task.type === "milestone" ? "stageGate" : task.type === "project" ? "phase" : "activity" });
  }, [getScheduleById]);

  const handleAddTaskFromGantt = useCallback(() => {
    // setEditingScheduleItemId(null);
    // setOpenDrawer("activity");
    handleOpenDrawer({ riskId: "", type: "activity" });
  }, []);

  const handleMenuItemClick = useCallback((type: DrawerType) => {
    // setOpenDrawer(type);
    // setStageGateId(null);
    handleOpenDrawer({ riskId: "", type: type });
  }, []);


  const handleTaskUpdate = useCallback(
    async (task: Task) => {
      try {
        const scheduleItem = getScheduleById(task.id as number);
        if (!scheduleItem || !task.start_date) return;

        const startDate = task.start_date;
        const endDate = calculateEndDate(startDate, task.duration || 0);

        await updateSchedule(scheduleItem.id, {
          planned_start_date: formatDateForAPI(startDate),
          planned_end_date: formatDateForAPI(endDate),
        });
      } catch (error) {
        console.error("Failed to update task dates:", error);
      }
    },
    [getScheduleById, updateSchedule]
  );

  const handleProgressUpdate = useCallback(
    async (task: Task) => {
      try {
        const scheduleItem = getScheduleById(task.id as number);
        if (!scheduleItem) return;

        const progressPercentage = Math.round((task.progress || 0) * 100);

        await updateSchedule(scheduleItem.id, {
          progress_percentage: progressPercentage.toString(),
          status: getStatusFromProgress(progressPercentage),
        });
      } catch (error) {
        console.error("Failed to update task progress:", error);
      }
    },
    [getScheduleById, updateSchedule]
  );

  const handleTaskReorder = useCallback(
    async (items: { id: number; sequence_order: number }[]) => {
      try {
        // transform to UpdateSchedulingItemPayload
        const payload: UpdateSchedulingItemPayload[] = items.map((item) => ({
          id: item.id,
          sequence_order: item.sequence_order,
        }));
        await updateMultipleSchedules(payload);
      } catch (error) {
        console.error("Failed to reorder tasks:", error);
      }
    },
    [updateMultipleSchedules]
  );

  const handleDeleteRequest = useCallback(() => {
    setDeleteConfirmOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!editingScheduleItem) return;

    setDeleteConfirmOpen(false);
    try {
      await deleteSchedule(editingScheduleItem.id);
      handleDrawerClose();
    } catch (error) {
      console.error("Failed to delete schedule item:", error);
    }
  }, [editingScheduleItem, deleteSchedule, handleDrawerClose]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmOpen(false);
  }, []);

  const handleSavePhase = useCallback(
    async (data: PhaseFormData) => {
      try {
        if (editingScheduleItem) {
          await updateSchedule(editingScheduleItem.id, {
            name: data.phase,
            planned_start_date: formatDateForAPI(data.startDate.toDate()),
            planned_end_date: formatDateForAPI(
              data.plannedCompletionDate.toDate()
            ),
            progress_percentage: data.progress.toString(),
            status: getStatusFromProgress(data.progress),
            color: data.color,
            user_id: data.userId || null,
          });
        } else {
          await createSchedule({
            parent_id: null,
            type: "phase",
            name: data.phase,
            phase_type_id: 1,
            planned_start_date: formatDateForAPI(data.startDate.toDate()),
            planned_end_date: formatDateForAPI(
              data.plannedCompletionDate.toDate()
            ),
            progress_percentage: data.progress.toString(),
            status: getStatusFromProgress(data.progress),
            color: data.color,
            user_id: data.userId || null,
          });
        }
        await refetch();
        handleDrawerClose();
      } catch (error) {
        console.error("Failed to save phase:", error);
      }
    },
    [editingScheduleItem, updateSchedule, createSchedule, handleDrawerClose]
  );

  const { removeCachePinned , removeCacheUnpinned} = useProjects({ searchKey: '', portfolioId: undefined, isPinned: false, quickFilters: { region: null, programme: null, site: null }, phase_name: null, phase_type_id: null, page: 1, limit: 10 });

  const { removeCache: removeOperationsCache } = useOperations({ projectId: projectId.toString() });
  const { removeCache: removeOperationsListCache } = useOperationsList({ projectId: projectId.toString() });

  const handleSaveActivity = useCallback(
    async (data: ActivityFormData) => {
      try {
        if (editingScheduleItem) {
          await updateSchedule(editingScheduleItem.id, {
            name: data.title,
            parent_id: data.phaseId || editingScheduleItem.parent_id,
            category: data.category_key || null,
            planned_start_date: formatDateForAPI(data.startDate.toDate()),
            planned_end_date: formatDateForAPI(data.plannedCompletionDate.toDate()),
            actual_end_date: data.actualCompletionDate ? formatDateForAPI(data.actualCompletionDate.toDate()) : null,
            progress_percentage: data.progress.toString(),
            status: getStatusFromProgress(data.progress),
            user_id: data.userId || null,
            is_recurring: data.is_recurring,
            frequency: data.frequency,
            description: data.description,
            asset_id: data.asset_id,
            ...(data.frequency === 'weekly' ? { recurring_day: data.recurring_day } : {}),
            ...(data.frequency === 'monthly' ? { monthly_pattern: data.monthly_pattern } : {})
          });
        } else {
          await createSchedule({
            parent_id: data.phaseId || null,
            type: "activity",
            name: data.title,
            category: data.category_key || null,
            planned_start_date: formatDateForAPI(data.startDate.toDate()),
            planned_end_date: formatDateForAPI(
              data.plannedCompletionDate.toDate()
            ),
            progress_percentage: data.progress.toString(),
            status: getStatusFromProgress(data.progress),
            role_id: 6,
            user_id: data.userId || null,
            asset_id: data.asset_id,
            is_recurring: data.is_recurring,
            frequency: data.frequency,
            description: data.description,
            ...(data.frequency === 'weekly' ? { recurring_day: data.recurring_day } : {}),
            ...(data.frequency === 'monthly' ? { monthly_pattern: data.monthly_pattern } : {})
          });
        }
        await refetch();
        removeCachePinned();
        removeCacheUnpinned();
        removeOperationsCache();
        removeOperationsListCache();
        handleDrawerClose();
      } catch (error) {
        console.error("Failed to save activity:", error);
      }
    },
    [editingScheduleItem, updateSchedule, createSchedule, handleDrawerClose]
  );

  const handleSaveStageGate = useCallback(
    async (data: StageGateFormData) => {
      try {
        if (editingScheduleItem) {
          await updateSchedule(editingScheduleItem.id, {
            name: data.title,
            parent_id: data.parentPhaseId || editingScheduleItem.parent_id,
            planned_end_date: formatDateForAPI(
              data.plannedCompletionDate.toDate()
            ),
            actual_end_date: data.actualCompletionDate
              ? formatDateForAPI(data.actualCompletionDate.toDate())
              : null,
            user_id: data.userId || null,
            description: data.description,
          });
        } else {
          await createSchedule({
            parent_id: data.parentPhaseId || null,
            type: "stage_gate",
            name: data.title,
            stage_gate_id: 2,
            planned_end_date: formatDateForAPI(
              data.plannedCompletionDate.toDate()
            ),
            status: "not_started",
            progress_percentage: "0",
            user_id: data.userId || null,
            description: data.description,
          });
        }
        await refetch();
        handleDrawerClose();
      } catch (error) {
        console.error("Failed to save stage gate:", error);
      }
    },
    [
      editingScheduleItem,
      updateSchedule,
      createSchedule,
      handleDrawerClose,
      refetch,
    ]
  );

  const handleLinkAdd = useCallback(
    async (link: Link) => {
      try {
        const ganttTypeToApiType: Record<string, DependencyType> = {
          "0": "finish_to_start",
          "1": "start_to_start",
          "2": "finish_to_finish",
          "3": "start_to_finish",
        };

        await createLinkMutation.mutateAsync({
          source_id: Number(link.source),
          target_id: Number(link.target),
          dependency_type: ganttTypeToApiType[link.type] || "start_to_start",
        });
      } catch (error) {
        console.error("Failed to create link:", error);
      }
    },
    [createLinkMutation]
  );

  const handleLinkDelete = useCallback(
    async (linkId: number) => {
      try {
        await deleteLinkMutation.mutateAsync(linkId);
      } catch (error) {
        console.error("Failed to delete link:", error);
      }
    },
    [deleteLinkMutation]
  );

  return {
    schedules,
    links,
    isLoading,
    isError,
    error,
    refetch,
    createSchedule,
    updateSchedule,
    updateMultipleSchedules,
    deleteSchedule,
    getScheduleById,
    getSchedulesByParentId,
    getSchedulesByType,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    openDrawer: type as DrawerType,
    stageGateId: id ? parseInt(id) : null,
    editingScheduleItem,
    deleteConfirmOpen,
    phaseOptions,
    phaseFormData,
    activityFormData,
    stageGateFormData,
    handleDrawerClose,
    handleTaskDblClick,
    handleAddTaskFromGantt,
    handleTaskUpdate,
    handleProgressUpdate,
    handleTaskReorder,
    handleDeleteRequest,
    handleDeleteConfirm,
    handleDeleteCancel,
    handleSavePhase,
    handleSaveActivity,
    handleSaveStageGate,
    handleMenuItemClick,
    handleLinkAdd,
    handleLinkDelete,
  };
};
